/**
 * In-app browser link interceptor.
 * Only intercepts links with data-nsfw="true" inside Instagram/FB/TikTok webviews.
 * On click → immediately fires the OS scheme to open Chrome (Android) or
 * Chrome/Safari (iOS). If after a short delay the user is still on this page,
 * Chrome is not installed → shows a small instructional overlay.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Copy, Check, X } from "lucide-react";

const detectInApp = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || window.opera || "";
  return /Instagram|FBAN|FBAV|FB_IAB|FBIOS|BytedanceWebview|TikTok/i.test(ua);
};

const isAndroidUA = () => /android/i.test(navigator.userAgent || "");
const isIOSUA = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

const ensureAbsoluteUrl = (raw) => {
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("tel:") || raw.startsWith("mailto:")) {
    return raw;
  }
  return "https://" + raw;
};

const InAppBrowserRedirect = () => {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pendingUrl, setPendingUrl] = useState("");
  const fallbackTimerRef = useRef(null);
  const isInAppRef = useRef(false);

  useEffect(() => {
    isInAppRef.current = detectInApp();
    if (!isInAppRef.current) return;

    const handleClick = (e) => {
      const link = e.target.closest("a[href]");
      if (!link) return;

      // Only intercept links explicitly marked as +18 (NSFW)
      if (link.getAttribute("data-nsfw") !== "true") return;

      const href = link.getAttribute("href");
      if (!href) return;
      if (href.startsWith("#") || href.startsWith("/") || href.startsWith("javascript:")) return;

      e.preventDefault();
      e.stopPropagation();

      const url = ensureAbsoluteUrl(href);

      // Fire the OS scheme IMMEDIATELY so Chrome/Safari opens without any UI.
      if (isAndroidUA()) {
        const stripped = url.replace(/^https?:\/\//i, "");
        const intentUrl =
          `intent://${stripped}` +
          `#Intent;scheme=https;package=com.android.chrome;` +
          `S.browser_fallback_url=${encodeURIComponent(url)};end`;
        window.location.href = intentUrl;
      } else if (isIOSUA()) {
        // Try Chrome iOS first (most aggressive). If not installed, the scheme
        // is silently ignored and we fall through to the fallback overlay below.
        const chromeUrl = "googlechrome://" + url.replace(/^https?:\/\//i, "");
        window.location.href = chromeUrl;
      }

      // If, after a short moment, we're still here → Chrome is not installed
      // OR the OS blocked the scheme. Show a small instructional overlay.
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = setTimeout(() => {
        if (document.visibilityState === "visible") {
          setPendingUrl(url);
          setShow(true);
          document.body.style.overflow = "hidden";
        }
      }, 1200);
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, []);

  const handleCopy = useCallback(async () => {
    const target = pendingUrl || window.location.href;
    try {
      await navigator.clipboard.writeText(target);
    } catch {
      const tmp = document.createElement("input");
      tmp.value = target;
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand("copy");
      document.body.removeChild(tmp);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [pendingUrl]);

  const close = () => {
    setShow(false);
    setPendingUrl("");
    document.body.style.overflow = "";
  };

  if (!show) return null;

  return (
    <>
      <style>{`
        @keyframes jg-bubble-enter {
          0% { opacity: 0; transform: scale(0.85) translateY(10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes jg-pulse-glow {
          0%, 100% { box-shadow: 0 6px 24px rgba(0,0,0,0.25); }
          50% { box-shadow: 0 6px 32px rgba(0,0,0,0.4); }
        }
      `}</style>

      <div
        data-testid="inapp-blocker-overlay"
        onClick={close}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 99999,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          background: "rgba(0,0,0,0.4)",
        }}
      />

      <div
        data-testid="inapp-speech-bubble"
        style={{
          position: "fixed",
          top: "60px",
          right: "16px",
          zIndex: 100000,
          animation: "jg-bubble-enter 0.4s ease-out",
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div
          style={{
            width: 0,
            height: 0,
            borderLeft: "12px solid transparent",
            borderRight: "12px solid transparent",
            borderBottom: "14px solid white",
            marginLeft: "auto",
            marginRight: "18px",
          }}
        />
        <div
          style={{
            background: "white",
            borderRadius: "14px",
            padding: "14px 16px",
            maxWidth: "230px",
            animation: "jg-pulse-glow 3s ease-in-out infinite",
            position: "relative",
          }}
        >
          <button
            onClick={close}
            aria-label="Cerrar"
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              border: "none",
              background: "transparent",
              color: "#666",
              cursor: "pointer",
              padding: 4,
              lineHeight: 0,
            }}
          >
            <X size={14} />
          </button>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: "800",
              color: "#000",
              lineHeight: "1.3",
              textAlign: "center",
            }}
          >
            Toca <span style={{ fontSize: "18px", letterSpacing: "3px" }}>···</span>
            <br />y abre en el navegador
          </p>
          <button
            data-testid="copy-link-btn"
            onClick={handleCopy}
            style={{
              marginTop: "12px",
              width: "100%",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "none",
              background: copied ? "#22c55e" : "#f0f0f0",
              color: copied ? "white" : "#555",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              transition: "all 0.3s ease",
            }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? "Copiado!" : "Copiar enlace"}
          </button>
        </div>
      </div>
    </>
  );
};

export default InAppBrowserRedirect;
